<template>
    <div>
        <div class="p-grid">
            <div class="p-col-12">
                <div class="card">
                    <Link :href="route('admin.roles')" class="text-blue-600 hover:underline">
                        ← Kembali ke Daftar Role
                    </Link>
                    <h2 class="text-2xl font-bold mt-2">{{ isEdit ? 'Edit Role' : 'Tambah Role Baru' }}</h2>
                </div>
            </div>
            
            <div class="p-col-12">
                <div class="card">
                    <form @submit.prevent="submitForm">
                        <div class="grid">
                            <!-- Basic Info -->
                            <div class="col-12 md:col-6">
                                <div class="field">
                                    <label for="kode" class="font-semibold">Kode *</label>
                                    <InputText id="kode" v-model="form.kode" class="w-full" 
                                               :class="{'p-invalid': errors.kode}" placeholder="contoh: pimpinan" />
                                    <small v-if="errors.kode" class="p-error">{{ errors.kode }}</small>
                                </div>
                            </div>
                            
                            <div class="col-12 md:col-6">
                                <div class="field">
                                    <label for="name" class="font-semibold">Nama *</label>
                                    <InputText id="name" v-model="form.name" class="w-full" 
                                               :class="{'p-invalid': errors.name}" placeholder="contoh: Pimpinan" />
                                    <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                                </div>
                            </div>
                            
                            <div class="col-12 md:col-6">
                                <div class="field">
                                    <label for="title" class="font-semibold">Title</label>
                                    <InputText id="title" v-model="form.title" class="w-full" 
                                               placeholder="contoh: Pimpinan Unit" />
                                </div>
                            </div>
                            
                            <div class="col-12 md:col-6">
                                <div class="field">
                                    <label for="icon" class="font-semibold">Icon (PrimeIcons)</label>
                                    <div class="p-inputgroup">
                                        <span class="p-inputgroup-addon">
                                            <i :class="'pi ' + form.icon"></i>
                                        </span>
                                        <InputText id="icon" v-model="form.icon" class="w-full" 
                                                   placeholder="contoh: pi-user" />
                                    </div>
                                </div>
                            </div>
                            
                            <div class="col-12 md:col-6">
                                <div class="field">
                                    <label for="scope_level" class="font-semibold">Scope Level</label>
                                    <Dropdown id="scope_level" v-model="form.scope_level" 
                                              :options="scopeLevelOptions" optionLabel="label" optionValue="value"
                                              placeholder="Pilih scope level" class="w-full" />
                                </div>
                            </div>
                            
                            <div class="col-12 md:col-6">
                                <div class="flex gap-4 mt-4">
                                    <div class="field-checkbox">
                                        <Checkbox id="has_scope" v-model="form.has_scope" :binary="true" />
                                        <label for="has_scope" class="ml-2">Has Scope</label>
                                    </div>
                                    <div class="field-checkbox">
                                        <Checkbox id="assignable" v-model="form.assignable" :binary="true" />
                                        <label for="assignable" class="ml-2">Assignable</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Menu Assignment -->
                        <div class="mt-6">
                            <h3 class="text-xl font-semibold mb-4">Assign Menu ke Role</h3>
                            <div class="border rounded-lg p-4 max-h-96 overflow-y-auto">
                                <Tree 
                                    :value="menuTree" 
                                    selectionMode="checkbox" 
                                    v-model:selectionKeys="selectedMenus"
                                    :expandedKeys="expandedKeys"
                                    @node-expand="onNodeExpand"
                                    @node-collapse="onNodeCollapse"
                                >
                                    <template #default="slotProps">
                                        <div class="flex items-center gap-2">
                                            <i :class="'pi ' + slotProps.node.icon" class="text-blue-500"></i>
                                            <span>{{ slotProps.node.label }}</span>
                                            <small v-if="slotProps.node.to" class="text-gray-400 ml-2">({{ slotProps.node.to }})</small>
                                        </div>
                                    </template>
                                </Tree>
                            </div>
                        </div>
                        
                        <!-- Submit -->
                        <div class="mt-6 flex gap-3">
                            <Button type="submit" :label="isEdit ? 'Update Role' : 'Simpan Role'" 
                                    icon="pi pi-save" class="p-button-primary" :loading="processing" />
                            <Link :href="route('admin.roles')">
                                <Button type="button" label="Batal" icon="pi pi-times" class="p-button-secondary" />
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Checkbox from "primevue/checkbox";
import Button from "primevue/button";
import Tree from "primevue/tree";
import { Link } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';

export default {
    name: "RoleForm",
    layout: AppLayout,
    components: {
        InputText,
        Dropdown,
        Checkbox,
        Button,
        Tree,
        Link,
    },
    props: {
        role: {
            type: Object,
            default: null,
        },
        menus: {
            type: Array,
            default: () => [],
        },
        errors: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            form: {
                name: '',
                kode: '',
                title: '',
                icon: 'pi-user',
                has_scope: false,
                assignable: false,
                scope_level: null,
                menus: [],
            },
            selectedMenus: {},
            expandedKeys: {},
            processing: false,
            scopeLevelOptions: [
                { label: 'Unit', value: 'unit' },
                { label: 'Fakultas', value: 'fakultas' },
                { label: 'Universitas', value: 'universitas' },
            ],
        };
    },
    computed: {
        isEdit() {
            return this.role !== null;
        },
        menuTree() {
            return this.transformMenusToTree(this.menus);
        },
    },
    mounted() {
        if (this.role) {
            this.form = {
                name: this.role.name,
                kode: this.role.kode,
                title: this.role.title || '',
                icon: this.role.icon || 'pi-user',
                has_scope: Boolean(this.role.has_scope),
                assignable: Boolean(this.role.assignable),
                scope_level: this.role.scope_level,
                menus: this.role.menus || [],
            };
            
            // Set selected menus from role
            this.initializeSelectedMenus();
        }
        
        // Expand all nodes by default
        this.expandAll();
    },
    methods: {
        transformMenusToTree(menus) {
            return menus.map(menu => ({
                key: menu.id.toString(),
                label: menu.text,
                icon: menu.icon || 'pi-circle',
                to: menu.to,
                data: menu,
                children: menu.children ? this.transformMenusToTree(menu.children) : undefined,
            }));
        },
        initializeSelectedMenus() {
            // Initialize from checked menus
            const setChecked = (items) => {
                items.forEach(item => {
                    if (item.checked) {
                        this.selectedMenus[item.id.toString()] = { checked: true, partialChecked: false };
                    }
                    if (item.children) {
                        setChecked(item.children);
                    }
                });
            };
            setChecked(this.menus);
        },
        expandAll() {
            const expand = (items) => {
                items.forEach(item => {
                    this.expandedKeys[item.id.toString()] = true;
                    if (item.children) {
                        expand(item.children);
                    }
                });
            };
            expand(this.menus);
            this.expandedKeys = { ...this.expandedKeys };
        },
        onNodeExpand(node) {
            this.expandedKeys[node.key] = true;
            this.expandedKeys = { ...this.expandedKeys };
        },
        onNodeCollapse(node) {
            delete this.expandedKeys[node.key];
            this.expandedKeys = { ...this.expandedKeys };
        },
        getSelectedMenuLabels() {
            const labels = [];
            const findLabels = (items) => {
                items.forEach(item => {
                    if (this.selectedMenus[item.id.toString()]?.checked) {
                        labels.push(item.text);
                    }
                    if (item.children) {
                        findLabels(item.children);
                    }
                });
            };
            findLabels(this.menus);
            return labels;
        },
        submitForm() {
            this.processing = true;
            
            const data = {
                ...this.form,
                menus: this.getSelectedMenuLabels(),
            };
            
            if (this.isEdit) {
                Inertia.put(this.route('admin.roles.update', this.role.id), data, {
                    onFinish: () => {
                        this.processing = false;
                    },
                });
            } else {
                Inertia.post(this.route('admin.roles.store'), data, {
                    onFinish: () => {
                        this.processing = false;
                    },
                });
            }
        },
    },
}
</script>

<style scoped>
.field {
    margin-bottom: 1rem;
}
</style>
